import notificationWithIcon from '@/utils/notification';
import apiCall from '@/store/apiCall';
import URLS from '@/store/urls';

const uploadSingleFile = async (file, folder = 'photo-gallery') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);
  const response = await apiCall({
    type: 'POST',
    headers: { 'content-type': 'multipart/form-data' },
    url: URLS.fileUpload,
    body: formData,
    isToken: true,
  });
  return response.data.data.url;
};

const actions = {
  // ---- Folders ----
  async getFolders({ commit }, companyId) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.photoFolder}/company/${companyId}`,
        isToken: true,
      });
      commit('SET_FOLDERS', Array.isArray(data) ? data : []);
    } catch (err) {
      if (!err.message.includes('404')) {
        const { response: { data: { message } } = { data: { message: err.message } } } = err;
        notificationWithIcon('error', message);
      }
    }
  },

  async getFoldersByIds({ commit }, ids) {
    try {
      if (!ids || ids.length === 0) {
        commit('SET_FOLDERS_BY_IDS', []);
        return [];
      }
      const { data: { data } } = await apiCall({
        type: 'POST',
        url: `${URLS.photoFolder}/by-ids`,
        body: { ids },
        isToken: true,
      });
      commit('SET_FOLDERS_BY_IDS', Array.isArray(data) ? data : []);
      return Array.isArray(data) ? data : [];
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
      return [];
    }
  },

  async createFolder({ dispatch }, payload) {
    try {
      const { companyId, coverFile, ...params } = payload;
      let coverImageUrl = params.coverImageUrl || '';

      if (coverFile?.file) {
        coverImageUrl = await uploadSingleFile(coverFile.file);
      }

      await apiCall({
        type: 'POST',
        url: URLS.photoFolder,
        body: { ...params, companyId, coverImageUrl },
        isToken: true,
      });
      notificationWithIcon('success', 'Folder created');
      dispatch('getFolders', companyId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },

  async updateFolder({ dispatch }, payload) {
    try {
      const {
        id, companyId, coverFile, ...params
      } = payload;

      if (coverFile?.file) {
        params.coverImageUrl = await uploadSingleFile(coverFile.file);
      }

      await apiCall({
        type: 'PUT',
        url: `${URLS.photoFolder}/${id}`,
        body: params,
        isToken: true,
      });
      notificationWithIcon('success', 'Folder updated');
      dispatch('getFolders', companyId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },

  async deleteFolder({ dispatch }, payload) {
    try {
      const { id, companyId } = payload;
      await apiCall({
        type: 'DELETE',
        url: `${URLS.photoFolder}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Folder deleted');
      dispatch('getFolders', companyId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },

  async updateFoldersOrder({ dispatch }, payload) {
    try {
      const { folders, companyId } = payload;
      await Promise.all(folders.map(({ id, order }) => apiCall({
        type: 'PUT',
        url: `${URLS.photoFolder}/${id}`,
        body: { order },
        isToken: true,
      })));
      notificationWithIcon('success', 'Order updated');
      dispatch('getFolders', companyId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },

  // ---- Images ----
  async getImages({ commit }, folderId) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.photoImage}/folder/${folderId}`,
        isToken: true,
      });
      commit('SET_IMAGES', Array.isArray(data) ? data : []);
    } catch (err) {
      if (!err.message.includes('404')) {
        const { response: { data: { message } } = { data: { message: err.message } } } = err;
        notificationWithIcon('error', message);
      }
    }
  },

  /**
   * Create a single image via upload-and-process pipeline.
   * Sends the file through Lambda for watermark + preview generation.
   * If no file is provided (imageUrl only), falls back to simple create.
   */
  async createImage({ dispatch }, payload) {
    try {
      const {
        folderId, companyId, imageFile, imageFileMobile, ...params
      } = payload;

      // If we have a raw file, use upload-and-process (Lambda pipeline)
      if (imageFile?.file) {
        const formData = new FormData();
        formData.append('file', imageFile.file);
        formData.append('folderId', String(folderId));
        formData.append('companyId', String(companyId));

        // Pass all text fields from the form
        if (params.title) formData.append('title', params.title);
        if (params.description) formData.append('description', params.description);
        if (params.url) formData.append('url', params.url);
        if (params.metaTitle) formData.append('metaTitle', params.metaTitle);
        if (params.metaDescription) formData.append('metaDescription', params.metaDescription);
        if (params.alt) formData.append('alt', params.alt);
        if (params.licenseType) formData.append('licenseType', params.licenseType);

        const { data } = await apiCall({
          type: 'POST',
          headers: { 'content-type': 'multipart/form-data' },
          url: `${URLS.photoImage}/upload-and-process`,
          body: formData,
          isToken: true,
        });

        notificationWithIcon('success', 'Image added');
        dispatch('getImages', folderId);

        const result = data?.data;
        if (result?.data) return result.data;
        return result || null;
      }

      // Fallback: no file (URL-only creation or edit with existing URL)
      const imageUrl = params.imageUrl || '';
      let imageUrlMobile = params.imageUrlMobile || '';

      if (imageFileMobile?.file) {
        imageUrlMobile = await uploadSingleFile(imageFileMobile.file);
      }

      const { data } = await apiCall({
        type: 'POST',
        url: URLS.photoImage,
        body: {
          ...params,
          folderId,
          companyId,
          imageUrl,
          imageUrlMobile: imageUrlMobile || imageUrl,
        },
        isToken: true,
      });
      notificationWithIcon('success', 'Image added');
      dispatch('getImages', folderId);
      const result = data?.data;
      if (result?.data) return result.data;
      return result || null;
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
      return null;
    }
  },

  async uploadImages({ dispatch }, payload) {
    try {
      const {
        newImages, folderId, companyId, order,
      } = payload;
      const imagesToUpload = newImages.filter((image) => image.file);
      if (imagesToUpload.length === 0) return;

      const uploadedUrls = await Promise.all(
        imagesToUpload.map((image) => uploadSingleFile(image.file)),
      );

      await Promise.all(uploadedUrls.map((imageUrl, index) => apiCall({
        type: 'POST',
        url: URLS.photoImage,
        body: {
          imageUrl,
          imageUrlMobile: imageUrl,
          folderId,
          companyId,
          url: `image-${Date.now()}-${index}`,
          order: (order || 0) + index,
        },
        isToken: true,
      })));

      notificationWithIcon('success', `${uploadedUrls.length} image(s) uploaded`);
      dispatch('getImages', folderId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },

  /**
   * Upload images through the full processing pipeline:
   * Upload original → Lambda (watermark + preview) → save to DB.
   * Falls back to simple upload if the endpoint isn't available yet.
   */
  async uploadAndProcessImages({ dispatch }, payload) {
    try {
      const {
        newImages, folderId, companyId, order,
      } = payload;
      const imagesToUpload = newImages.filter((image) => image.file);
      if (imagesToUpload.length === 0) return;

      let successCount = 0;

      // Sequential upload: one by one to avoid overloading Lambda
      // eslint-disable-next-line no-restricted-syntax
      for (let i = 0; i < imagesToUpload.length; i += 1) {
        try {
          const formData = new FormData();
          formData.append('file', imagesToUpload[i].file);
          formData.append('folderId', String(folderId));
          formData.append('companyId', String(companyId));
          formData.append('url', `image-${Date.now()}-${i}`);

          // eslint-disable-next-line no-await-in-loop
          await apiCall({
            type: 'POST',
            headers: { 'content-type': 'multipart/form-data' },
            url: `${URLS.photoImage}/upload-and-process`,
            body: formData,
            isToken: true,
          });
          successCount += 1;
        } catch (err) {
          // If endpoint not available, fall back to simple upload
          if (err?.response?.status === 404) {
            // eslint-disable-next-line no-await-in-loop
            const imageUrl = await uploadSingleFile(imagesToUpload[i].file);
            // eslint-disable-next-line no-await-in-loop
            await apiCall({
              type: 'POST',
              url: URLS.photoImage,
              body: {
                imageUrl,
                imageUrlMobile: imageUrl,
                folderId,
                companyId,
                url: `image-${Date.now()}-${i}`,
                order: (order || 0) + i,
              },
              isToken: true,
            });
            successCount += 1;
          } else {
            throw err;
          }
        }
      }

      notificationWithIcon('success', `${successCount} image(s) uploaded and processed`);
      dispatch('getImages', folderId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },

  async updateImage({ dispatch }, payload) {
    try {
      const {
        id, folderId, imageFile, imageFileMobile, ...params
      } = payload;

      // If a new file is provided, re-upload through Lambda pipeline first
      if (imageFile?.file) {
        const formData = new FormData();
        formData.append('file', imageFile.file);

        await apiCall({
          type: 'PUT',
          headers: { 'content-type': 'multipart/form-data' },
          url: `${URLS.photoImage}/${id}/reupload`,
          body: formData,
          isToken: true,
        });
        // Remove image URL fields — they were just updated by reupload
        delete params.imageUrl;
        delete params.imageUrlMobile;
      }

      if (imageFileMobile?.file) {
        params.imageUrlMobile = await uploadSingleFile(imageFileMobile.file);
      }

      // Update remaining text fields
      await apiCall({
        type: 'PUT',
        url: `${URLS.photoImage}/${id}`,
        body: params,
        isToken: true,
      });
      notificationWithIcon('success', 'Image updated');
      if (folderId) dispatch('getImages', folderId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },

  /**
   * Fetch license count for a single photo image.
   * Called by PhotoGalleries-page.vue → confirmDeleteImage() to determine
   * whether to show the "licensed image" warning (30-day download window)
   * or a simple delete confirmation.
   * Returns 0 on error to allow deletion to proceed without blocking.
   *
   * @see admin.photo-image.controller.ts — GET :id/license-info endpoint
   * @see PhotoGalleries-page.vue — confirmDeleteImage() method
   */
  async getLicenseInfo(_, { id }) {
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.photoImage}/${id}/license-info`,
        isToken: true,
      });
      return data?.licensesCount || 0;
    } catch {
      return 0;
    }
  },

  async deleteImage({ dispatch }, payload) {
    try {
      const { id, folderId } = payload;
      await apiCall({
        type: 'DELETE',
        url: `${URLS.photoImage}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Image deleted');
      dispatch('getImages', folderId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },

  async updateImagesOrder({ dispatch }, payload) {
    try {
      const { images, folderId } = payload;
      await Promise.all(images.map(({ id, order }) => apiCall({
        type: 'PUT',
        url: `${URLS.photoImage}/${id}`,
        body: { order },
        isToken: true,
      })));
      notificationWithIcon('success', 'Order updated');
      dispatch('getImages', folderId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },

  /**
   * Run AI analysis on an existing image and return suggested fields.
   * Does not auto-apply — admin reviews suggestions.
   */
  async aiAnalyzeImage(_, imageId) {
    try {
      const { data } = await apiCall({
        type: 'POST',
        url: `${URLS.photoImage}/${imageId}/ai-analyze`,
        isToken: true,
      });
      // API may return { data: { data: {...} } } or { data: {...} }
      const result = data?.data;
      if (result && !Array.isArray(result) && result.data && typeof result.data === 'object') {
        return result.data;
      }
      return result || null;
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', `AI analysis failed: ${message}`);
      return null;
    }
  },

  /**
   * Run AI analysis on a raw file (before saving to DB).
   * Sends the file as multipart form data.
   */
  async aiAnalyzeFile(_, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiCall({
        type: 'POST',
        url: `${URLS.photoImage}/analyze-file`,
        body: formData,
        headers: { 'content-type': 'multipart/form-data' },
        isToken: true,
      });
      // API may return { data: { data: {...} } } or { data: {...} }
      const result = data?.data;
      if (result && !Array.isArray(result) && result.data && typeof result.data === 'object') {
        return result.data;
      }
      return result || null;
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', `AI analysis failed: ${message}`);
      return null;
    }
  },

  /**
   * Bulk upload with progress tracking — sequential, one image at a time.
   * Flow: AI analyze file → get fields → upload with populated fields.
   * Mirrors single-image upload where AI runs first, then data is sent.
   * @param {Function} onProgress - callback(current, total, filename, status)
   */
  async bulkUploadWithProgress({ dispatch }, payload) {
    const {
      files, folderId, companyId, useAi = false, onProgress,
    } = payload;

    let succeeded = 0;
    let failed = 0;
    const total = files.length;
    const createdImages = [];

    // eslint-disable-next-line no-restricted-syntax
    for (let i = 0; i < files.length; i += 1) {
      const fileItem = files[i];
      const file = fileItem.file || fileItem;
      const filename = file.name || `image-${i + 1}`;

      if (onProgress) {
        onProgress(i, total, filename, useAi ? 'analyzing' : 'processing');
      }

      try {
        let aiAnalysis = null;

        // Step 1: Run AI analysis on the raw file (like single upload does)
        if (useAi) {
          try {
            // eslint-disable-next-line no-await-in-loop
            aiAnalysis = await dispatch('aiAnalyzeFile', file);
          } catch (err) {
            console.warn(`AI analysis failed for ${filename}, uploading without AI:`, err.message);
          }
        }

        if (onProgress) {
          onProgress(i, total, filename, 'uploading');
        }

        // Step 2: Build FormData with AI-populated fields
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folderId', String(folderId));
        formData.append('companyId', String(companyId));

        // If AI provided analysis, include the fields so backend doesn't need to re-analyze
        if (aiAnalysis) {
          if (aiAnalysis.title) formData.append('title', aiAnalysis.title);
          if (aiAnalysis.description) formData.append('description', aiAnalysis.description);
          if (aiAnalysis.alt) formData.append('alt', aiAnalysis.alt);
          if (aiAnalysis.metaTitle) formData.append('metaTitle', aiAnalysis.metaTitle);
          if (aiAnalysis.metaDescription) formData.append('metaDescription', aiAnalysis.metaDescription);
          // Generate URL slug from AI title
          if (aiAnalysis.title) {
            const slug = aiAnalysis.title
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .replace(/^-|-$/g, '');
            if (slug) formData.append('url', slug);
          }
          // Don't set useAi — AI was already done on client side
        } else {
          // Fallback URL if no AI
          formData.append('url', `image-${Date.now()}-${i}`);
          if (useAi) formData.append('useAi', 'true');
        }

        // eslint-disable-next-line no-await-in-loop
        const response = await apiCall({
          type: 'POST',
          headers: { 'content-type': 'multipart/form-data' },
          url: `${URLS.photoImage}/upload-and-process`,
          body: formData,
          isToken: true,
        });

        // Extract created image for tag attachment
        const result = response?.data?.data;
        const created = result?.data || result;
        if (created?.id) {
          createdImages.push({ image: created, suggestedTags: aiAnalysis?.suggestedTags || [] });
        }

        succeeded += 1;
      } catch (err) {
        failed += 1;
        console.error(`Failed to upload ${filename}:`, err.message);
      }
    }

    // Step 3: Attach AI-suggested tags to created images
    // eslint-disable-next-line no-restricted-syntax
    for (const { image, suggestedTags } of createdImages) {
      if (suggestedTags.length > 0) {
        try {
          // Create missing tags and get IDs
          const tagIds = [];
          // eslint-disable-next-line no-restricted-syntax
          for (const tagName of suggestedTags) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const tag = await dispatch('createTag', { name: tagName });
              if (tag?.id) tagIds.push(tag.id);
            } catch {
              // Tag might already exist, try to find it
            }
          }
          if (tagIds.length > 0) {
            // eslint-disable-next-line no-await-in-loop
            await dispatch('setImageTags', { imageId: image.id, tagIds });
          }
        } catch (err) {
          console.warn(`Failed to attach tags to image ${image.id}:`, err.message);
        }
      }
    }

    if (onProgress) {
      onProgress(total, total, null, 'completed');
    }

    if (succeeded > 0) {
      notificationWithIcon(
        'success',
        `Uploaded ${succeeded} of ${total} image(s)${failed > 0 ? `, ${failed} failed` : ''}`,
      );
    } else {
      notificationWithIcon('error', `All ${total} uploads failed`);
    }

    // Reload images list after all uploads complete
    dispatch('getImages', folderId);

    return {
      status: 'completed', succeeded, failed, total,
    };
  },

  /* ------------------------------------------------------------------ */
  /*  Tags                                                               */
  /* ------------------------------------------------------------------ */

  async fetchAllTags() {
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: URLS.photoImageTags,
        isToken: true,
      });
      // API may return { data: [...] } or { data: { data: [...] } }
      const result = data?.data;
      if (Array.isArray(result)) return result;
      if (Array.isArray(result?.data)) return result.data;
      return [];
    } catch (err) {
      notificationWithIcon('error', 'Failed to load tags');
      return [];
    }
  },

  async createTag(_, { name }) {
    try {
      const { data } = await apiCall({
        type: 'POST',
        url: URLS.photoImageTags,
        body: { name },
        isToken: true,
      });
      // Unwrap: API may return { data: { data: { id, name } } } or { data: { id, name } }
      const result = data?.data;
      if (result && result.data && result.data.id) return result.data;
      return result || null;
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      notificationWithIcon('error', msg);
      return null;
    }
  },

  async updateTag(_, { id, name }) {
    try {
      const { data } = await apiCall({
        type: 'PUT',
        url: `${URLS.photoImageTags}/${id}`,
        body: { name },
        isToken: true,
      });
      // Unwrap: API may return { data: { data: { id, name } } } or { data: { id, name } }
      const result = data?.data;
      if (result && result.data && result.data.id) return result.data;
      return result || null;
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      notificationWithIcon('error', msg);
      return null;
    }
  },

  async deleteTag(_, { id }) {
    try {
      const { data } = await apiCall({
        type: 'DELETE',
        url: `${URLS.photoImageTags}/${id}`,
        isToken: true,
      });
      return data;
    } catch (err) {
      notificationWithIcon('error', 'Failed to delete tag');
      return null;
    }
  },

  async getTagImageCount(_, { id }) {
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.photoImageTags}/${id}/image-count`,
        isToken: true,
      });
      // Unwrap: API may return { data: { data: { imageCount } } } or { data: { imageCount } }
      const result = data?.data;
      if (result?.data?.imageCount !== undefined) return result.data.imageCount;
      return result?.imageCount || 0;
    } catch {
      return 0;
    }
  },

  async getImageTags(_, { imageId }) {
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.photoImageTags}/image/${imageId}`,
        isToken: true,
      });
      const result = data?.data;
      if (Array.isArray(result)) return result;
      if (Array.isArray(result?.data)) return result.data;
      return [];
    } catch {
      return [];
    }
  },

  async setImageTags(_, { imageId, tagIds }) {
    try {
      const { data } = await apiCall({
        type: 'PUT',
        url: `${URLS.photoImageTags}/image/${imageId}`,
        body: { tagIds },
        isToken: true,
      });
      const result = data?.data;
      if (Array.isArray(result)) return result;
      if (Array.isArray(result?.data)) return result.data;
      return [];
    } catch (err) {
      notificationWithIcon('error', 'Failed to update image tags');
      return [];
    }
  },
};

export default actions;
